import { cn } from "./cn";

/**
 * A bar of views across the top of a page.
 *
 * Extracted from `Settings.tsx`, which had the same underlined bar written by
 * hand. There were already three different tab bars in the app -- underlined in
 * Settings, pills in Notifications, a joined segmented control on the
 * Dashboard -- and adding a fourth for the AM dashboard would have made the
 * product look assembled from parts.
 *
 * The underline is the right one of the three to keep for a page-level view
 * switcher: pills read as filters that could be combined, and a segmented
 * control reads as a setting rather than as navigation.
 *
 * ## Not a routing component
 *
 * It takes a value and calls back. Whether a tab is a piece of local state, a
 * query parameter or a route is the page's business, and baking `NavLink` in
 * here would have forced every user of it to invent a URL.
 */
export interface Tab<T extends string> {
  value: T;
  label: string;
  /** Shown after the label, dimmer. For a count of what is behind the tab. */
  badge?: string | number;
}

export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  className,
}: {
  tabs: readonly Tab<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Names the bar for a screen reader, e.g. "Account manager views". */
  label: string;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      /* One row that scrolls sideways on a phone, rather than wrapping: a
         wrapped bar is two rows of tabs with the underline on whichever one
         the active tab fell into, and reads as two bars. The scrollbar is
         hidden because a swipe is how a phone scrolls a strip anyway. */
      className={cn(
        "flex gap-1 border-b border-line md:flex-wrap",
        "max-md:overflow-x-auto max-md:[scrollbar-width:none]",
        className,
      )}
    >
      {tabs.map((tab) => {
        const active = tab.value === value;

        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.value)}
            className={cn(
              /* `-mb-px` pulls the active underline over the container's own
                 border so they read as one line rather than two. Not on a
                 phone, where the bar scrolls and so clips at its edge: the
                 overlap would only make it scroll a pixel vertically too. */
              /* `whitespace-nowrap` so a two-word label stays one tab when
                 a phone squeezes the bar, rather than becoming a tab twice
                 the height of its neighbours. */
              "-mb-px flex shrink-0 items-center max-md:mb-0 gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm",
              "transition-colors",
              active
                ? "border-brand-600 font-medium text-content"
                : "border-transparent text-muted hover:text-brand-600 dark:hover:text-brand-400",
            )}
          >
            {tab.label}
            {tab.badge !== undefined && tab.badge !== "" ? (
              <span
                className={cn(
                  "tabular-nums text-[11px]",
                  active ? "text-muted" : "text-faint",
                )}
              >
                {tab.badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
